/* UserSession.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const ConnectionHandlerFactory = require("@gluenetworks/platform-sdk-remote").connectionFactories
    .ConnectionHandlerFactory;

/**
 * @fires UserSession#OrganizationChange - fired when the organization has changed.
 * @fires UserSession#ActiveOrganizationChange - fired when the organization has changed.
 * @fires UserSession#BeforeLogout - fired just before logout action.
 */

class UserSession extends EventEmitter {
    constructor(logger, UserEntity) {
        super();
        this._logger = logger;
        this._UserEntityDefinition = UserEntity;
        this._userEntity = undefined;
        this._activeOrganization = null;
        this._token = null;
        this._ConnectionHandlerFactory = ConnectionHandlerFactory;
        this._connection = undefined;
    }
    _triggerBeforeLogout(reason) {
        /**
         * @event UserSession#BeforeLogout
         * @param {string} - logout reason
         */
        this.emit("BeforeLogout", reason);
    }
    /*******************************************/
    /* START LISTENER REGISTRATIONS METHODS */
    /*******************************************/
    _registerConnectionListeners() {
        this._connection.on(
            "ConnectionFailed",
            (this._onConnectionFailed = this._onConnectionFailed.bind(this))
        );
        this._connection.on(
            "UnAuthenticated",
            (this._onUnAuthenticated = this._onUnAuthenticated.bind(this))
        );
        this._connection.on(
            "NotAuthenticated",
            (this._onNotAuthenticated = this._onNotAuthenticated.bind(this))
        );
        this._connection.on(
            "ConnectionLost",
            (this._onConnectionLost = this._onConnectionLost.bind(this))
        );
        this._connection.on(
            "ConnectionShutdown",
            (this._onConnectionShutdown = this._onConnectionShutdown.bind(this))
        );
        this._connection.on(
            "ReconnectionAttempt",
            (this._onReconnectionAttempt = this._onReconnectionAttempt.bind(this))
        );
        this._connection.on(
            "ReconnectionEstablished",
            (this._onReconnectionEstablished = this._onReconnectionEstablished.bind(this))
        );
        this._connection.on(
            "ReconnectionFailed",
            (this._onReconnectionFailed = this._onReconnectionFailed.bind(this))
        );
    }

    _unregisterConnectionListeners() {
        this._connection.removeListener("ConnectionFailed", this._onConnectionFailed);
        this._connection.removeListener("UnAuthenticated", this._onUnAuthenticated);
        this._connection.removeListener("NotAuthenticated", this._onNotAuthenticated);
        this._connection.removeListener("ConnectionLost", this._onConnectionLost);
        this._connection.removeListener("ConnectionShutdown", this._onConnectionShutdown);
        this._connection.removeListener("ReconnectionAttempt", this._onReconnectionAttempt);
        this._connection.removeListener("ReconnectionEstablished", this._onReconnectionEstablished);
        this._connection.removeListener("ReconnectionFailed", this._onReconnectionFailed);
    }
    /*******************************************/
    /* END LISTENER REGISTRATIONS METHODS */
    /* START HANDLER METHODS */
    /*******************************************/
    _onConnectionFailed() {
        this._logger.warn({}, "Socket failed to connect. Browser reload required.");
    }
    _onConnectionLost() {
        this._logger.warn({}, "Socket disconnection occurred. Browser reload required.");
    }

    _onConnectionShutdown() {
        this._logger.warn({}, "Socket disconnection occurred. Browser reload required.");
    }

    _onNotAuthenticated() {
        this._logger.warn({}, "Request requires the connection to have an authenticated user.");
    }
    _onReconnectionAttempt() {
        this._logger.warn({}, "Socket attempting to reconnect");
    }
    _onReconnectionEstablished() {
        this._logger.warn({}, "Socket reconnect successful");
    }
    _onReconnectionFailed() {
        this._logger.warn({}, "Socket reconnect failed");
    }
    _onUnAuthenticated(cause) {
        this._unregisterConnectionListeners();
        this._token = null;
        this._userEntity = null;
        this._logger.warn({cause: cause}, "Socket no longer has an authenticated user.");
    }
    /*******************************************/
    /* END HANDLER API METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/
    _connectAndAuthenticate(creds, systemUri, port = 443, path = "/ControlApi/socket.io") {
        this._connection = this._ConnectionHandlerFactory.getInstance("socket", systemUri, port, {
            path: path,
        });

        return this._connection
            .connect()
            .then(() => {
                return this._connection.authenticate(creds.username, creds.password);
            })
            .then((response) => {
                this._registerConnectionListeners();
                this._token = response.token;
                return this._UserEntityDefinition.retrieveByOtk(this._token);
            })
            .then((user) => {
                this._userEntity = user;

                let orgToBeSelected = {
                    name: this._userEntity.authorizedTenants[this._userEntity.tenantId].name,
                    id: this._userEntity.tenantId,
                };

                this.setActiveOrganization(orgToBeSelected);
            });
    }
    /*******************************************/
    /* END PRIVATE API METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/
    isConnectedAndAuthenticated() {
        if (
            this._connection &&
            this._connection.isConnected() &&
            this._connection.isAuthenticated()
        ) {
            return true;
        } else {
            return false;
        }
    }
    login(creds, opts) {
        let preAuthPromise = Promise.resolve();
        if (this.isConnectedAndAuthenticated()) {
            let prevLoggedInUser = _.get(this._userEntity, "userName");
            let newLoggedInUser = _.get(creds, "username");
            if (prevLoggedInUser && newLoggedInUser && newLoggedInUser === prevLoggedInUser) {
                return Promise.resolve(this._userEntity);
            }
            preAuthPromise = this._connection.unauthenticate();
        }
        let systemUri = _.get(opts, "systemUri");
        let port = _.get(opts, "port");
        let path = _.get(opts, "path");
        return preAuthPromise.then(() => {
            return this._connectAndAuthenticate(creds, systemUri, port, path);
        });
    }

    logout(details) {
        let reason = _.has(details, "timeout") ? "timeout" : "requested";
        this._triggerBeforeLogout(reason);
        return this._connection.unauthenticate(reason);
    }

    changeOrganization(orgObj) {
        return this.setActiveOrganization(orgObj);
    }

    get token() {
        return this.getToken();
    }
    getToken() {
        return this._token;
    }

    get user() {
        return this.getUser();
    }
    getUser() {
        return this._userEntity;
    }

    get activeOrganization() {
        return this.getActiveOrganization();
    }

    set activeOrganization(orgObj) {
        this.setActiveOrganization(orgObj);
    }

    getActiveOrganization() {
        return this._activeOrganization;
    }

    setActiveOrganization(orgObj, silent) {
        const oldOrg = _.clone(this._activeOrganization);
        this._activeOrganization = orgObj;
        if (!silent) {
            this.emit("ActiveOrganizationChange", this._activeOrganization, oldOrg);
            this.emit("OrganizationChange", this._activeOrganization, oldOrg);
        }
        return this;
    }

    get connection() {
        return this.getConnection();
    }
    getConnection() {
        return this._connection;
    }

    shutdown() {
        this._unregisterListeners();
        this._logger = undefined;
        this._UserEntityDefinition = undefined;
        this._userEntity = undefined;
        this._activeOrganization = undefined;
        this._token = undefined;
        this._connection = undefined;
    }
    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}

module.exports = UserSession;
